
import React from 'react';
import { Button, ButtonGroup, IInputGroupProps } from '@blueprintjs/core';
import { FieldInput, FieldMeta, FormFieldInputType, FormFieldMetaType } from '@pinto/react-form';
import { Assign } from 'utility-types';
import _ from 'lodash';

import TextInput from './TextInput';

export type NumericInputProps = Assign<Omit<IInputGroupProps, 'onChange' | 'value'>, {
    value: number | null;
    onChange: (value: number | null) => void;
    onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    changeOnBlur?: boolean;
    min?: number;
    max?: number;
    step?: number;
    rightElement?: React.ReactNode;
    small?: boolean;
    noButtons?: boolean;
    /**
     * Sets the width of the input to the smallest value
     * Good for 1-2 digit values or 1 digit floats (i.e. 0 -> 100 or 0.0 -> 9.9)
     */
    short?: boolean;
    /**
     * Style is applied to the `<input />` component **and NOT** to the tag istself
     * This is a limitation of the underlying blueprint <InputGroup /> component
     * Use `className` to update container styles
     */
    style?: React.CSSProperties;
}>;

const ALL_ZERO_PRECISION = /\.0*$/;

export type NumericInputFieldProps = (
    Omit<NumericInputProps, 'value' | 'onChange'>
    & {
        input: FieldInput<number>;
        meta: FieldMeta;
        onChange?: NumericInputProps['onChange'];
    }
);

/**
 * Reimplementation of blueprint's NumericInput with a few changes
 *
 * Q: Why not just use the default NumericInput?
 * A: It's plagued with several issues and really weird design choices
 *  - precision only goes to 3 decimal points
 *  - the component throws an exception in several instances (stepSize > minStepSize, min > max)
 */
export class NumericInput extends React.PureComponent<NumericInputProps> {
    static getDerivedStateFromProps(nextProps: any, nextState: any) {
        if (nextProps.value === nextState.propsValue) return null;

        return {
            value: defaultFormatter(nextProps.value),
            propsValue: nextProps.value,
        };
    }

    static defaultProps = {
        min: 0,
        step: 1,
    }

    state = {
        value: '',
    }

    get precision() {
        const { step } = this.props;
        return !step || step >= 1 ? 0 : Math.ceil(-1 * Math.log10(step))
    }

    parse(value: string | null, { round = true } = {}): number | null {
        const { step, min, max } = this.props;

        if (value == null) return null;
        if (ALL_ZERO_PRECISION.test(value)) return null;

        let parsed = parseFloat(value);
        if (Number.isNaN(parsed)) return null;

        if (round && step) {
            parsed = _.round(Math.round(parsed / step) * step, this.precision);
        }

        return _.clamp(parsed, min || 0, max || Infinity);
    }

    change(text: string | null, options?: { round: boolean }) {
        const { value, onChange } = this.props;

        if (text == null) text = '';
        this.setState(() => ({ value: text }));

        if (text) {
            const parsed = this.parse(text, options);
            this.setState({ value: parsed == null ? text : String(parsed) });
            if (parsed != null && parsed !== value) onChange(parsed);
        }
    }

    changeStep(direction: 1 | -1) {
        const { value, step, min } = this.props;
        const finalMin = min || 0;
        const finalStep = step || 1;
        const nextStep = String(value == null ? finalMin : value + finalStep);
        const prevStep = String(value == null ? finalMin : value - finalStep);
        this.change(direction > 0 ? nextStep : prevStep);
    }

    onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        let prevent = true;
        switch (event.key) {
            case 'ArrowUp': this.changeStep(+1); break;
            case 'ArrowDown': this.changeStep(-1); break;
            default:
                prevent = false;
        }

        if (prevent) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    onChange = (text: string | null) => {
        const { changeOnBlur } = this.props;
        if (!changeOnBlur) this.change(text, { round: false });
    }

    onBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { onBlur } = this.props;
        let { value } = event.target;
        value = value.replace(ALL_ZERO_PRECISION, '');
        this.change(value);
        if (onBlur) onBlur(event);
    }

    render() {
        const {
            value,
            onChange,
            onBlur,
            min,
            max,
            step,
            rightElement,
            small,
            short,
            noButtons,
            className,
            ...rest
        } = this.props;

        return <Root
            fill={false}
            {...rest}
            Tag={TextInput}
            value={this.state.value}
            onChange={this.onChange}
            onBlur={this.onBlur}
            onKeyDown={this.onKeyDown}
            className={_.filter([
                short && 'short',
                small && 'small',
                className
            ]).join(' ')}
            rightElement={
                <ButtonGroup>
                    {rightElement}
                    {!noButtons &&
                        <Control Tag={ButtonGroup} vertical>
                            <Button
                                intent={rest.intent}
                                icon='chevron-up'
                                onClick={() => this.changeStep(+1)}
                                disabled={max != null && value != null && value + 1 > max}
                            />
                            <Button
                                intent={rest.intent}
                                icon='chevron-down'
                                onClick={() => this.changeStep(-1)}
                                disabled={min != null && value != null && value - 1 < min}
                            />
                        </Control>
                    }
                </ButtonGroup>
            }
        />
    }
}

export class NumericInputField extends React.PureComponent<NumericInputFieldProps> {
    static propTypes = {
        input: FormFieldInputType.isRequired,
        meta: FormFieldMetaType.isRequired,
    };

    handleChange: NumericInputProps['onChange'] = value => {
        const { input, onChange } = this.props;

        input.onChange(value);
        if (onChange) onChange(value);
    }

    render() {
        const { input, meta, ...props } = this.props;

        if (meta.touched && meta.error) props.intent = 'danger';

        return <NumericInput
            {...props}
            value={input.value}
            onChange={this.handleChange}
        />
    }
}

export default NumericInput;

export const defaultParser = (value: number | null): number | null => {
    if (value == null) value = null;
    else if (Number.isNaN(value)) value = 0;

    return value;
};

export const defaultFormatter = (value?: string | number | null): string => {
    if (value == null) value = '';
    else if (Number.isNaN(value)) value = '0';
    else if (value && (value as string)[0] === '.') value = '0' + value;

    return String(value);
};


require('!style-loader/lib/addStyles.js')([[ module.id, ".flex {\n  display: flex;\n}\n.flex.flex-space-between {\n  align-items: space-between;\n}\n.center {\n  text-align: center;\n}\n.bold {\n  font-weight: bold;\n}\n.mt0 {\n  margin-top: 0rem !important;\n}\n.mb0 {\n  margin-bottom: 0rem !important;\n}\n.ml0 {\n  margin-left: 0rem !important;\n}\n.mr0 {\n  margin-right: 0rem !important;\n}\n.m0 {\n  margin: 0rem 0rem 0rem 0rem !important;\n}\n.mx0 {\n  margin-left: 0rem !important;\n  margin-right: 0rem !important;\n}\n.my0 {\n  margin-top: 0rem !important;\n  margin-bottom: 0rem !important;\n}\n.mt1 {\n  margin-top: 1rem !important;\n}\n.mb1 {\n  margin-bottom: 1rem !important;\n}\n.ml1 {\n  margin-left: 1rem !important;\n}\n.mr1 {\n  margin-right: 1rem !important;\n}\n.m1 {\n  margin: 1rem 1rem 1rem 1rem !important;\n}\n.mx1 {\n  margin-left: 1rem !important;\n  margin-right: 1rem !important;\n}\n.my1 {\n  margin-top: 1rem !important;\n  margin-bottom: 1rem !important;\n}\n.mt2 {\n  margin-top: 2rem !important;\n}\n.mb2 {\n  margin-bottom: 2rem !important;\n}\n.ml2 {\n  margin-left: 2rem !important;\n}\n.mr2 {\n  margin-right: 2rem !important;\n}\n.m2 {\n  margin: 2rem 2rem 2rem 2rem !important;\n}\n.mx2 {\n  margin-left: 2rem !important;\n  margin-right: 2rem !important;\n}\n.my2 {\n  margin-top: 2rem !important;\n  margin-bottom: 2rem !important;\n}\n.mt05 {\n  margin-top: 0.5rem !important;\n}\n.mb05 {\n  margin-bottom: 0.5rem !important;\n}\n.ml05 {\n  margin-left: 0.5rem !important;\n}\n.mr05 {\n  margin-right: 0.5rem !important;\n}\n.m05 {\n  margin: 0.5rem 0.5rem 0.5rem 0.5rem !important;\n}\n.mx05 {\n  margin-left: 0.5rem !important;\n  margin-right: 0.5rem !important;\n}\n.my05 {\n  margin-top: 0.5rem !important;\n  margin-bottom: 0.5rem !important;\n}\n.mt025 {\n  margin-top: 0.25rem !important;\n}\n.mb025 {\n  margin-bottom: 0.25rem !important;\n}\n.ml025 {\n  margin-left: 0.25rem !important;\n}\n.mr025 {\n  margin-right: 0.25rem !important;\n}\n.m025 {\n  margin: 0.25rem 0.25rem 0.25rem 0.25rem !important;\n}\n.mx025 {\n  margin-left: 0.25rem !important;\n  margin-right: 0.25rem !important;\n}\n.my025 {\n  margin-top: 0.25rem !important;\n  margin-bottom: 0.25rem !important;\n}\n.pt0 {\n  padding-top: 0rem !important;\n}\n.pb0 {\n  padding-bottom: 0rem !important;\n}\n.pl0 {\n  padding-left: 0rem !important;\n}\n.pr0 {\n  padding-right: 0rem !important;\n}\n.p0 {\n  padding: 0rem 0rem 0rem 0rem !important;\n}\n.px0 {\n  padding-left: 0rem !important;\n  padding-right: 0rem !important;\n}\n.py0 {\n  padding-top: 0rem !important;\n  padding-bottom: 0rem !important;\n}\n.pt1 {\n  padding-top: 1rem !important;\n}\n.pb1 {\n  padding-bottom: 1rem !important;\n}\n.pl1 {\n  padding-left: 1rem !important;\n}\n.pr1 {\n  padding-right: 1rem !important;\n}\n.p1 {\n  padding: 1rem 1rem 1rem 1rem !important;\n}\n.px1 {\n  padding-left: 1rem !important;\n  padding-right: 1rem !important;\n}\n.py1 {\n  padding-top: 1rem !important;\n  padding-bottom: 1rem !important;\n}\n.pt2 {\n  padding-top: 2rem !important;\n}\n.pb2 {\n  padding-bottom: 2rem !important;\n}\n.pl2 {\n  padding-left: 2rem !important;\n}\n.pr2 {\n  padding-right: 2rem !important;\n}\n.p2 {\n  padding: 2rem 2rem 2rem 2rem !important;\n}\n.px2 {\n  padding-left: 2rem !important;\n  padding-right: 2rem !important;\n}\n.py2 {\n  padding-top: 2rem !important;\n  padding-bottom: 2rem !important;\n}\n.pt05 {\n  padding-top: 0.5rem !important;\n}\n.pb05 {\n  padding-bottom: 0.5rem !important;\n}\n.pl05 {\n  padding-left: 0.5rem !important;\n}\n.pr05 {\n  padding-right: 0.5rem !important;\n}\n.p05 {\n  padding: 0.5rem 0.5rem 0.5rem 0.5rem !important;\n}\n.px05 {\n  padding-left: 0.5rem !important;\n  padding-right: 0.5rem !important;\n}\n.py05 {\n  padding-top: 0.5rem !important;\n  padding-bottom: 0.5rem !important;\n}\n.pt025 {\n  padding-top: 0.25rem !important;\n}\n.pb025 {\n  padding-bottom: 0.25rem !important;\n}\n.pl025 {\n  padding-left: 0.25rem !important;\n}\n.pr025 {\n  padding-right: 0.25rem !important;\n}\n.p025 {\n  padding: 0.25rem 0.25rem 0.25rem 0.25rem !important;\n}\n.px025 {\n  padding-left: 0.25rem !important;\n  padding-right: 0.25rem !important;\n}\n.py025 {\n  padding-top: 0.25rem !important;\n  padding-bottom: 0.25rem !important;\n}\n.font-small-1 {\n  font-size: 0.857142857142857rem;\n}\n.font-large-1 {\n  font-size: 1.142857142857143rem;\n}\n.font-small-2 {\n  font-size: 0.714285714285714rem;\n}\n.font-large-2 {\n  font-size: 1.285714285714286rem;\n}\n.font-small-3 {\n  font-size: 0.571428571428571rem;\n}\n.font-large-3 {\n  font-size: 1.428571428571429rem;\n}\n.font-small-4 {\n  font-size: 0.428571428571429rem;\n}\n.font-large-4 {\n  font-size: 1.571428571428571rem;\n}\n.NumericInput-Root--2pbwi {\n  display: inline-flex;\n  width: 7rem;\n}\n.NumericInput-Root--2pbwi.small input {\n  border-radius: 0;\n  border: none;\n  box-shadow: none;\n  max-height: 1.2rem;\n}\n.NumericInput-Root--2pbwi.small .bp3-button-group {\n  vertical-align: top;\n}\n.NumericInput-Root--2pbwi.small button {\n  border-radius: 0 !important;\n  height: 0.6rem;\n  overflow: hidden;\n  white-space: nowrap;\n  text-overflow: ellipsis;\n}\n.NumericInput-Root--2pbwi.short {\n  width: 5.25rem;\n}\n.NumericInput-Control--E80Z- .bp3-button {\n  min-width: unset !important;\n  max-width: unset !important;\n  min-height: unset !important;\n  max-height: unset !important;\n  width: 1.75rem !important;\n  height: 15px;\n  margin: 0 !important;\n}\n", '' ]]);
const { RSL_COMPONENT } = require("!../../node_modules/@pinto/react-style-loader/react-style-loader.runtime.compiled.js");
const __Root = RSL_COMPONENT('Root', 'NumericInputRoot', 'NumericInput-Root--2pbwi');
 const Root = __Root
const __Control = RSL_COMPONENT('Control', 'NumericInputControl', 'NumericInput-Control--E80Z-');
 const Control = __Control

